"use client";

import { ArrowRight, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const reviews = [{
  id: 1,
  initials: "AM",
  name: "Arthur Miller",
  date: "[2024-05-12]",
  quote: "Moscariello Construction transformed our outdated property into a modern masterpiece. Their attention to detail and structural integrity is unmatched in Collegeville."
}, {
  id: 2,
  initials: "BK",
  name: "Barbara K.",
  date: "[2024-04-20]",
  quote: "Professional, punctual, and precise. Americo and his team managed our renovation project with complete transparency. The quality of work is simply exceptional."
}, {
  id: 3,
  initials: "RJ",
  name: "Robert J.",
  date: "[2024-03-15]",
  quote: "We trust Moscariello with all our property management needs. They handle every maintenance issue with the same care as if it were their own home."
}, {
  id: 4,
  initials: "LT",
  name: "Linda T.",
  date: "[2024-02-28]",
  quote: "Straightforward communication and honest pricing. They didn't try to upsell us on unnecessary features, just delivered exactly what we needed on time."
}, {
  id: 5,
  initials: "DS",
  name: "David S.",
  date: "[2024-01-10]",
  quote: "Emergency structural repairs were handled with incredible speed. The team was clean, efficient, and kept us informed throughout the entire process."
}, {
  id: 6,
  initials: "EH",
  name: "Elena H.",
  date: "[2023-12-05]",
  quote: "The craftsmanship is evident in every corner of our new addition. Moscariello Construction sets the standard for quality building in Pennsylvania."
}];
export default function Testimonials() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [api, setApi] = useState<CarouselApi>();
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  useEffect(() => {
    if (!api) return;
    const update = () => {
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };
    update();
    api.on("select", update);
    api.on("reInit", update);
    return () => {
      api.off("select", update);
    };
  }, [api]);
  return <section data-section-id="1768" id="voices-of-satisfaction" className="bg-background text-foreground relative overflow-hidden">
      {}
      <div className="h-6 w-full" style={{
      backgroundImage: "repeating-linear-gradient(45deg, hsl(215 15% 85% / 0.2) 0 2px, transparent 2px 6px), repeating-linear-gradient(-45deg, hsl(215 15% 85% / 0.2) 0 2px, transparent 2px 6px)",
      backgroundColor: "var(--background)"
    }} aria-hidden="true" />

      <div ref={ref} className="container mx-auto max-w-[1440px] px-[5vw] py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-4">
            <div className={`lg:sticky lg:top-24 transition-all duration-800 ease-[cubic-bezier(0.16,1,0.3,1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[30px]"}`}>
              {}
              <div className="flex items-center gap-3 mb-8">
                <span className="inline-block w-2.5 h-2.5" style={{
                backgroundColor: "var(--secondary)"
              }} aria-hidden="true" />
                <span className="font-mono text-[0.75rem] font-semibold uppercase tracking-[0.2em]" style={{
                color: "var(--secondary)"
              }}>Voices of Satisfaction</span>
              </div>

              {}
              <h2 className="font-serif font-normal uppercase text-foreground" style={{
              fontSize: "clamp(2rem, 5vw, 3.5rem)",
              lineHeight: "1.1",
              letterSpacing: "-0.02em"
            }}>
                Client <span className="italic">Testimonials</span> & Project Feedback.
              </h2>

              {}
              <div className="mt-10 space-y-4">
                <div className="flex items-center gap-3">
                  <svg viewBox="0 0 48 48" className="w-7 h-7" aria-hidden="true">
                    <path fill="currentColor" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.5-8 19.5-20 0-1.3-.1-2.4-.4-3.5z" />
                  </svg>
                  <span className="font-sans font-bold uppercase text-foreground text-lg tracking-wide">
                    Client Reviews
                  </span>
                </div>

                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-3">
                    <span className="inline-block w-2 h-2" style={{
                    backgroundColor: "var(--secondary)"
                  }} aria-hidden="true" />
                    <span className="font-mono text-sm uppercase text-foreground" style={{
                    letterSpacing: "0.12em"
                  }}>
                      5.0 / 5.0 Rating
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="inline-block w-2 h-2" style={{
                    backgroundColor: "var(--secondary)"
                  }} aria-hidden="true" />
                    <span className="font-mono text-sm uppercase text-foreground" style={{
                    letterSpacing: "0.12em"
                  }}>
                      Quality Assured
                    </span>
                  </div>
                </div>

                {}
                <div className="pt-6">
                  <Link to="/ConnectWithUsPage" className="group inline-flex items-center gap-2 font-mono text-sm uppercase text-foreground hover:text-secondary transition-colors duration-200" style={{
                  letterSpacing: "0.12em"
                }}>
                    <span>Share your experience</span>
                    <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" style={{
                    color: "var(--secondary)"
                  }} />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8 min-w-0">
            <div className={`transition-all duration-800 ease-[cubic-bezier(0.16,1,0.3,1)] delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[30px]"}`}>
              <Carousel opts={{
              align: "start",
              loop: false
            }} setApi={setApi} className="w-full">
                <CarouselContent className="-ml-4">
                  {reviews.map((review, i) => <CarouselItem key={review.id} data-index={i} className="pl-4 basis-full md:basis-1/2 lg:basis-[60%]">
                      <article className="relative h-full p-8 border border-border" style={{
                    backgroundColor: "var(--card)"
                  }}>
                        <div className="flex flex-col h-full">
                          {}
                          <div className="flex items-center gap-1" aria-label="5 out of 5 stars">
                            {[0, 1, 2, 3, 4].map(s => <Star key={s} className="w-[18px] h-[18px]" style={{
                          color: "var(--secondary)",
                          fill: "var(--secondary)"
                        }} strokeWidth={1.5} />)}
                          </div>

                          {}
                          <p className="font-sans mt-6 text-[1.125rem] leading-[1.6] flex-1" style={{
                        color: "var(--foreground)"
                      }}>
                            &ldquo;{review.quote}&rdquo;
                          </p>

                          {}
                          <div className="h-px w-full my-8 bg-border" aria-hidden="true" />

                          {}
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="flex items-center justify-center w-10 h-10 shrink-0 bg-foreground text-background">
                                <span className="font-sans font-bold text-sm uppercase">
                                  {review.initials}
                                </span>
                              </div>
                              <span className="font-sans font-bold text-sm truncate">
                                {review.name}
                              </span>
                            </div>
                            <span className="font-mono text-xs shrink-0 text-muted-foreground">
                              {review.date}
                            </span>
                          </div>
                        </div>
                      </article>
                    </CarouselItem>)}
                </CarouselContent>
              </Carousel>

              {}
              <div className="flex items-center justify-end gap-3 mt-10">
                <Button variant="outline" onClick={() => api?.scrollPrev()} disabled={!canPrev} aria-label="Previous review" className="w-14 h-14 p-0 border border-border bg-transparent hover:bg-secondary hover:text-black disabled:opacity-30 transition-colors duration-200">
                  <ChevronLeft className="w-6 h-6" strokeWidth={2} />
                </Button>
                <Button variant="outline" onClick={() => api?.scrollNext()} disabled={!canNext} aria-label="Next review" className="w-14 h-14 p-0 border border-border bg-transparent hover:bg-secondary hover:text-black disabled:opacity-30 transition-colors duration-200">
                  <ChevronRight className="w-6 h-6" strokeWidth={2} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}